import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Skeleton from "components/ui/Skeleton";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ArticleRow from "./knowledge-base-hub/ArticleRow";
import FilterPills from "./knowledge-base-hub/FilterPills";
import useKnowledgeHub from "./knowledge-base-hub/useKnowledgeHub";
import { FILTERS, isUnread } from "./knowledge-base-hub/knowledge-filters";
import ChapterHeader from "./chapter-view/ChapterHeader";
import ChapterSidebar from "./chapter-view/ChapterSidebar";

const ChapterView = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const { loading, chapters, articles, toggleBookmark } = useKnowledgeHub();

  const chapterIndex = chapters.findIndex((c) => String(c.id) === String(chapterId));
  const chapter = chapters[chapterIndex];

  const chapterArticles = useMemo(
    () => articles.filter((a) => String(a.chapter.id) === String(chapterId)),
    [articles, chapterId]
  );
  const visible = chapterArticles.filter(FILTERS[filter].test);
  const nextUnread = chapterArticles.find(isUnread);

  const goHub = () => navigate("/swiftboard/knowledge-base");
  const openArticle = (id) => navigate(`/swiftboard/knowledge-base/${id}`);
  const jumpTo = (id) => document.getElementById(`kb-article-${id}`)?.scrollIntoView({ block: "center" });

  if (loading) {
    return (
      <div className="h-full overflow-y-auto bg-surface_alt">
        <div className="max-w-[100rem] mx-auto w-full py-3 space-y-5">
          <Skeleton className="h-40 w-full" rounded="rounded-2xl" />
          <Skeleton className="h-96 w-full" rounded="rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="h-full overflow-y-auto bg-surface_alt flex items-center justify-center">
        <NoDataState
          icon="folder"
          title="Chapter Not Found"
          message="This chapter may have been removed or is no longer available."
          showRefresh={false}
          showBack={true}
          onBack={goHub}
          size="medium"
        />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-surface_alt">
      <div className="max-w-[100rem] mx-auto w-full py-3 space-y-5">
        <ChapterHeader
          chapter={chapter}
          number={chapterIndex + 1}
          articles={chapterArticles}
          nextUnread={nextUnread}
          onBack={goHub}
          onOpen={openArticle}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-5 items-start">
          <div className="hidden lg:block lg:sticky lg:top-0">
            <ChapterSidebar articles={chapterArticles} onJump={jumpTo} />
          </div>

          <div className="space-y-4 min-w-0">
            <FilterPills
              articles={chapterArticles}
              value={filter}
              onChange={setFilter}
              keys={["all", "unread", "new", "saved"]}
              labels={{ saved: "Saved" }}
            />
            {visible.length === 0 ? (
              <NoDataState
                icon="folder"
                title="Nothing here yet"
                message="No articles match this filter."
                showRefresh={false}
                showBack={false}
                showCreate={false}
                size="small"
              />
            ) : (
              <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                {visible.map((a) => (
                  <div key={a.id} id={`kb-article-${a.id}`}>
                    <ArticleRow
                      article={a}
                      onOpen={openArticle}
                      onToggleBookmark={toggleBookmark}
                      showExcerpt
                      showChapter={false}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterView;
